import {
  BreakpointConfigType,
  Breakpoints,
  CTA,
  Disclaimer,
  DisclaimerModal,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextStyle,
  TextTag,
  TextWeight,
  useIsMobile,
} from '@vw-marketing/us-components';
import React from 'react';
import { BadgeIcon } from '../../../components/icons/BadgeIcon';
import ClockIcon from '../../../components/icons/ClockIcon';
import IncentiveConstants from '../../../hooks-store/incentive-store/actions/incentive-constants';
import { useStore } from '../../../hooks-store/store';
import {
  DealerModel,
  OfferInformation,
  OffersModel,
} from '../../../hooks-store/typings/incentive-store';
import { useFeatureAppConfig } from '../../../hooks/use-feature-app-config';
import { useFeatureAppContent } from '../../../hooks/use-feature-app-content';
import { useIsNationalOffers } from '../../../hooks/use-is-national-offer';
import { useTrackingManager } from '../../../hooks/use-tracking-manager';
import { useTranslations } from '../../../hooks/use-translations';
import {
  onHandleBasicFormButtonLink,
  onHandleFormButtonTracking,
} from '../../../tagging/tagging-helpers';
import { AppConstants } from '../../../utils/app-constants';
import { formatSalesEventDate, separateThousand } from '../../../utils/general';
import SalesEventMlpLogo from '../sales-event-mlp-logo';
import ModelTrimHeader from './model-trim-header';
import {
  BadgeIconWrapper,
  ButtonsContainer,
  CardWrapper,
  ClockIconWrapper,
  ColumnContentWrapper,
  ContentWrapper,
  DateContentWrapper,
  HighlightContentWrapper,
  MainContentWrapper,
  NumericContentWrapper,
  NumericDataWrapper,
  OfferConditionsWrapper,
  OfferContentWrapper,
  OfferDescription,
  OtherOfferTypeContentWrapper,
  PaymentContentWrapper,
  PaymentSectionWrapper,
  RootWrapper,
  SecondayColorTextWrapper,
  StyledDisclaimerContainer,
  ViewInventoryButton,
  ViewOffersButton,
} from './styled';

interface SalesOfferCardProps {
  offerInformation: OfferInformation;
}

// Common textStyles for TextCustom components
const textStyles: {
  [key: string]: TextStyle | BreakpointConfigType<TextStyle>;
} = {
  label: {
    [Breakpoints.default]: {
      fontSize: TextSize.TextSize14,
      lineHeight: TextLineHeight.TextLineHeight16,
    },
    [Breakpoints.b1600]: {
      fontSize: TextSize.TextSize16,
      lineHeight: TextLineHeight.TextLineHeight20,
    },
  },
  label200: {
    [Breakpoints.default]: {
      fontSize: TextSize.TextSize16,
      lineHeight: TextLineHeight.TextLineHeight20,
    },
    [Breakpoints.b1600]: {
      fontSize: TextSize.TextSize16,
      lineHeight: TextLineHeight.TextLineHeight20,
    },
  },
  copy200: {
    [Breakpoints.default]: {
      fontSize: TextSize.TextSize20,
      lineHeight: TextLineHeight.TextLineHeight32,
      fontWeight: TextWeight.bold,
    },
    [Breakpoints.b1600]: {
      fontSize: TextSize.TextSize24,
      lineHeight: TextLineHeight.TextLineHeight36,
      fontWeight: TextWeight.bold,
    },
  },
  headline300: {
    [Breakpoints.default]: {
      fontSize: TextSize.TextSize32,
      lineHeight: TextLineHeight.TextLineHeight36,
      fontWeight: TextWeight.bold,
    },
    [Breakpoints.b1600]: {
      fontSize: TextSize.TextSize48,
      lineHeight: TextLineHeight.TextLineHeight56,
      fontWeight: TextWeight.bold,
    },
  },
  headline350Bold: {
    [Breakpoints.default]: {
      fontSize: TextSize.TextSize20,
      lineHeight: TextLineHeight.TextLineHeight24,
      fontWeight: TextWeight.bold,
    },
    [Breakpoints.b1600]: {
      fontSize: TextSize.TextSize24,
      lineHeight: TextLineHeight.TextLineHeight28,
      fontWeight: TextWeight.bold,
    },
  },
  copy200Small: {
    [Breakpoints.default]: {
      fontSize: TextSize.TextSize16,
      lineHeight: TextLineHeight.TextLineHeight25,
    },
    [Breakpoints.b1600]: {
      fontSize: TextSize.TextSize20,
      lineHeight: TextLineHeight.TextLineHeight40,
    },
  },
  copy200SmallSecondary: {
    [Breakpoints.default]: {
      fontSize: TextSize.TextSize16,
      lineHeight: TextLineHeight.TextLineHeight24,
    },
    [Breakpoints.b1600]: {
      fontSize: TextSize.TextSize20,
      lineHeight: TextLineHeight.TextLineHeight32,
    },
  },
};

const SalesOfferCard: React.FC<SalesOfferCardProps> = ({
  offerInformation,
}) => {
  const salesEventType = offerInformation.specialOffer?.dealOfferTypes[0].toLowerCase();

  const amount =
    salesEventType === 'apr'
      ? `${offerInformation.specialOffer?.customContent?.aprValue ?? '0.0'}`
      : separateThousand(offerInformation.specialOffer?.dealPricePerMonth ?? 0);

  const termValue =
    salesEventType === 'apr'
      ? offerInformation.specialOffer?.customContent?.financeTermLength ?? ''
      : offerInformation.specialOffer?.customContent?.leaseTermLength ?? 0;

  const offerHighlight =
    offerInformation.specialOffer?.customContent?.offerHighlight;
  const dealEndDate = formatSalesEventDate(
    offerInformation.specialOffer?.dealEndDate ?? '',
  );
  const legalRequirements = offerInformation.specialOffer?.dealSubtitle;

  const offerTitle = offerInformation.specialOffer?.dealTitle;

  const trackingManager = useTrackingManager();
  const { sales_event_logo_url_light } = useFeatureAppContent();
  const translations = useTranslations();
  const isSalesEvent = Boolean(sales_event_logo_url_light);
  const [
    store,
    { [IncentiveConstants.SetModalFormData]: setModalFormData },
  ] = useStore();
  const isNationalOffersOnly = useIsNationalOffers();
  const { vehicle } = useFeatureAppConfig();
  const isMobile = useIsMobile(Breakpoints.b960);

  const vehicleDealer: DealerModel = vehicle?.dealer
    ? ({
        ...vehicle.dealer,
      } as any)
    : store.dealer;

  const goToInventory = (offer: OfferInformation) => {
    const inventoryURL: string = `/en/inventory.html/__app/inventory/results/${offer.slug}.app`;

    window.open(inventoryURL, '_blank');

    onHandleBasicFormButtonLink(
      offer,
      'View inventory',
      trackingManager,
      store,
      inventoryURL,
    );
  };

  const setNationalOffersOnlyFormData = (offer: OffersModel) => {
    setModalFormData(
      offer,
      undefined,
      undefined,
      AppConstants.NationalOffersOnlyFormType,
      AppConstants.NationalOffersOnlyFormName,
    );
  };

  const setNotStandardFormData = (offer: OffersModel) => {
    setModalFormData(
      offer,
      undefined,
      vehicleDealer,
      AppConstants.NotStandardModelFormType,
      AppConstants.NotStandardModelFormName,
    );
  };

  const isNotStandardModel = (offer: OffersModel) =>
    typeof offer.modelCustomCta !== 'undefined';

  const setFormData = (specialOffer: OffersModel) => {
    if (isNationalOffersOnly) {
      setNationalOffersOnlyFormData(specialOffer);
    } else if (isNotStandardModel(specialOffer)) {
      setNotStandardFormData(specialOffer);
    } else {
      setModalFormData(
        specialOffer,
        undefined,
        vehicleDealer,
        AppConstants.OfferDetailsFormType,
        AppConstants.OfferDetailsFormName,
      );
    }
    onHandleFormButtonTracking(
      specialOffer,
      'View offers details',
      trackingManager,
      store,
    );
  };

  const showOfferType =
    offerInformation.specialOffer?.customContent?.showOfferType === 'yes';

  return (
    <RootWrapper role="sales-offer-card">
      {salesEventType !== 'lease' && (
        <StyledDisclaimerContainer>
          <DisclaimerModal
            disclaimers={
              ([
                {
                  description:
                    offerInformation.specialOffer?.customContent
                      ?.offerImageDisclaimer ?? '',
                  key: '1',
                  token: '1',
                  number: '1',
                },
              ] as unknown) as Disclaimer[]
            }
          />
        </StyledDisclaimerContainer>
      )}
      <CardWrapper isSalesEvent={isSalesEvent} className="card-wrapper">
        {!isMobile && (
          <SalesEventMlpLogo
            sales_event_logo_url_light={sales_event_logo_url_light}
            title={translations.salesEventMlp.title}
          />
        )}
        <ContentWrapper className="content-wrapper">
          {isMobile && (
            <SalesEventMlpLogo
              sales_event_logo_url_light={sales_event_logo_url_light}
              title={translations.salesEventMlp.title}
            />
          )}
          <ModelTrimHeader offerInformation={offerInformation} />
          <MainContentWrapper>
            <OfferConditionsWrapper>
              <PaymentSectionWrapper>
                {salesEventType === 'lease' || salesEventType === 'apr' ? (
                  <PaymentContentWrapper
                    isAprSalesEvent={salesEventType === 'apr'}
                    className="payment-wrapper"
                  >
                    <ColumnContentWrapper>
                      {/* Lease Payment/APR Header */}
                      {showOfferType && (
                        <TextCustom
                          appearance={TextAppearance.label150}
                          tag={TextTag.p}
                          style={textStyles.label}
                        >
                          <SecondayColorTextWrapper>
                            {salesEventType === 'apr'
                              ? translations.salesEvent.cardContent.aprLabel
                              : translations.salesEvent.cardContent
                                  .leasePaymentLabel}
                          </SecondayColorTextWrapper>
                        </TextCustom>
                      )}
                      <NumericContentWrapper>
                        <NumericDataWrapper>
                          {/* Dollar Sign */}
                          {showOfferType && salesEventType === 'lease' && (
                            <TextCustom
                              appearance={TextAppearance.copy200}
                              tag={TextTag.p}
                              style={textStyles.copy200}
                            >
                              {'$'}
                            </TextCustom>
                          )}
                          {/* Amount */}
                          <TextCustom
                            appearance={TextAppearance.headline300}
                            tag={TextTag.p}
                            style={textStyles.headline300}
                          >
                            {amount}
                          </TextCustom>
                        </NumericDataWrapper>
                        {/* Monthly Label */}
                        {showOfferType && salesEventType === 'lease' && (
                          <TextCustom
                            appearance={TextAppearance.copy200}
                            tag={TextTag.p}
                            style={textStyles.copy200Small}
                          >
                            <SecondayColorTextWrapper>
                              {translations.salesEvent.cardContent.monthlyLabel}
                            </SecondayColorTextWrapper>
                          </TextCustom>
                        )}
                      </NumericContentWrapper>
                    </ColumnContentWrapper>
                    {showOfferType && (
                      <ColumnContentWrapper>
                        {/* Term Header */}
                        <TextCustom
                          appearance={TextAppearance.label150}
                          tag={TextTag.p}
                          style={textStyles.label}
                        >
                          <SecondayColorTextWrapper>
                            {translations.salesEvent.cardContent.termLabel}
                          </SecondayColorTextWrapper>
                        </TextCustom>
                        <NumericContentWrapper>
                          <NumericDataWrapper>
                            {/* Term Value */}
                            <TextCustom
                              appearance={TextAppearance.headline300}
                              tag={TextTag.p}
                              style={textStyles.headline300}
                            >
                              {termValue}
                            </TextCustom>
                          </NumericDataWrapper>
                          {/* Months Label */}
                          <TextCustom
                            appearance={TextAppearance.copy200}
                            tag={TextTag.p}
                            style={textStyles.copy200Small}
                          >
                            <SecondayColorTextWrapper>
                              {translations.salesEvent.cardContent.monthsLabel}
                            </SecondayColorTextWrapper>
                          </TextCustom>
                        </NumericContentWrapper>
                      </ColumnContentWrapper>
                    )}
                  </PaymentContentWrapper>
                ) : (
                  <OtherOfferTypeContentWrapper>
                    <TextCustom
                      appearance={TextAppearance.headline300}
                      tag={TextTag.p}
                      style={textStyles.headline350Bold}
                    >
                      {offerTitle}
                    </TextCustom>
                  </OtherOfferTypeContentWrapper>
                )}
                {offerHighlight && (
                  <HighlightContentWrapper>
                    <BadgeIconWrapper>
                      <BadgeIcon />
                    </BadgeIconWrapper>
                    <TextCustom
                      appearance={TextAppearance.copy200}
                      tag={TextTag.p}
                      style={textStyles.copy200SmallSecondary}
                    >
                      {offerHighlight}
                    </TextCustom>
                  </HighlightContentWrapper>
                )}
              </PaymentSectionWrapper>
              <DateContentWrapper>
                <ClockIconWrapper>
                  <ClockIcon />
                </ClockIconWrapper>
                <TextCustom
                  appearance={TextAppearance.copy200}
                  tag={TextTag.p}
                  style={textStyles.copy200SmallSecondary}
                >
                  <SecondayColorTextWrapper>
                    {translations.salesEvent.cardContent.offerEndsLabel}
                  </SecondayColorTextWrapper>{' '}
                  {dealEndDate}
                </TextCustom>
              </DateContentWrapper>
            </OfferConditionsWrapper>
            <OfferContentWrapper>
              <OfferDescription>
                <TextCustom
                  appearance={TextAppearance.copy200}
                  tag={TextTag.p}
                  style={textStyles.copy200SmallSecondary}
                >
                  <SecondayColorTextWrapper>
                    {legalRequirements}
                  </SecondayColorTextWrapper>
                </TextCustom>
              </OfferDescription>
              <ButtonsContainer>
                <ViewOffersButton>
                  <CTA
                    aria-label="View offer details CTA"
                    emphasis="offer"
                    onClick={() => setFormData(offerInformation.specialOffer!)}
                    tag="button"
                  >
                    <TextCustom
                      appearance={TextAppearance.label200}
                      tag={TextTag.p}
                      style={textStyles.label200}
                    >
                      {
                        translations.salesEvent.cardContent
                          .viewOfferDetailsCTALabel
                      }
                    </TextCustom>
                  </CTA>
                </ViewOffersButton>
                <ViewInventoryButton>
                  <CTA
                    tag="a"
                    emphasis="tertiary"
                    aria-label="Go to inventory CTA"
                    onClick={() => goToInventory(offerInformation)}
                    target="_blank"
                    stretchContent
                  >
                    <TextCustom
                      appearance={TextAppearance.label200}
                      tag={TextTag.p}
                      style={textStyles.label200}
                    >
                      {
                        translations.salesEvent.cardContent
                          .viewInventoryCTALabel
                      }
                    </TextCustom>
                  </CTA>
                </ViewInventoryButton>
              </ButtonsContainer>
            </OfferContentWrapper>
          </MainContentWrapper>
        </ContentWrapper>
      </CardWrapper>
    </RootWrapper>
  );
};

export default SalesOfferCard;
